import { Icon } from '@iconify/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { useState } from 'react'
import { toast } from 'react-toastify'

import { changePassword } from '~features/change-password/api'
import { DialogComponent, Input, Label } from '~in-app/components'
import { changePasswordValidation } from '~in-app/validation/changePassword.validation'

type ChangePasswordDialogProps = {
  isOpenInitially: boolean
  onClose: () => void
}

export const ChangePasswordDialog = ({ isOpenInitially, onClose }: ChangePasswordDialogProps) => {
  const formOpts = formOptions({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
    },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: changePasswordValidation,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value)
    },
  })
  const [isVisible, toggleVisibility] = useState(false)
  const formControl = useForm(formOpts)
  const changePasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      toast.success('Password changed successfully', { position: 'top-center', hideProgressBar: false, autoClose: 500 })
      onClose()
      formControl.reset()
    },
    onError: (error: Error) => {
      toast.error(`Error Changing Password: ${error.message}`, { position: 'top-center', hideProgressBar: false, autoClose: 500 })
    },
  })

  const onSubmit = async (data: any) => {
    try {
      changePasswordMutation.mutate(data)
    }
    catch (error) {
      console.error('Password change failed:', error)
      toast.error('Password change failed', {
        position: 'top-center',
        hideProgressBar: false,
        autoClose: 500,
      })
    }
  }

  return (
    <form
      noValidate
      onSubmit={
        (e) => {
          e.preventDefault()
          formControl.handleSubmit()
        }
      }
    >

      <DialogComponent
        isOpen={isOpenInitially}
        submitButtonLabel="Change"
        title="Change Password"
        onClose={onClose}
        onSubmit={() => formControl.handleSubmit()}
      >

        <div className="mb-2 grid gap-2">
          <Label
            required
            className="text-sm"
            htmlFor="currentPassword"
          >
            Current Password
          </Label>

          {/* Use the Field component to handle rendering */}
          <formControl.Field
            name="currentPassword"
            validators={
              {
                onChange: changePasswordValidation.shape.currentPassword,
              }
            }
          >
            {
              field => (
                <>
                  <div className="relative flex w-full">
                    <Input
                      className="pr-10"
                      id="currentPassword"
                      type={isVisible ? 'text' : 'password'}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e => field.handleChange(e.target.value)}
                    />

                    {/* Placeholder for the eye toggle button logic */}
                    <button
                      className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center text-sm"
                      type="button"
                      onClick={() => toggleVisibility(!isVisible)}
                    >
                      <Icon
                        height={24}
                        icon={isVisible ? 'mdi:eye' : 'mdi:eye-off'}
                        width={24}
                      />
                    </button>
                  </div>

                  {
                    field.state.meta.errors && (
                      <em
                        className="error-message"
                        role="alert"
                      >
                        {field.state.meta.errors.join(', ')}
                      </em>
                    )
                  }
                </>
              )
            }
          </formControl.Field>
        </div>

        <div className="mt-2 grid gap-2">
          <Label
            required
            className="text-sm"
            htmlFor="newPassword"
          >
            New Password
          </Label>

          {/* Use the Field component to handle rendering */}
          <formControl.Field
            name="newPassword"
            validators={
              {
                onChange: changePasswordValidation.shape.newPassword,
              }
            }
          >
            {
              field => (
                <>
                  <div className="relative flex w-full">
                    <Input
                      className="pr-10"
                      id="newPassword"
                      type={isVisible ? 'text' : 'password'}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={e => field.handleChange(e.target.value)}
                    />

                    {/* Placeholder for the eye toggle button logic */}
                    <button
                      className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center text-sm"
                      type="button"
                      onClick={() => toggleVisibility(!isVisible)}
                    >
                      <Icon
                        height={24}
                        icon={isVisible ? 'mdi:eye' : 'mdi:eye-off'}
                        width={24}
                      />
                    </button>
                  </div>

                  {
                    field.state.meta.errors && (
                      <em
                        className="error-message"
                        role="alert"
                      >
                        {field.state.meta.errors.join(', ')}
                      </em>
                    )
                  }
                </>
              )
            }
          </formControl.Field>
        </div>
      </DialogComponent>

    </form>
  )
}
